<template>
  <a-menu
    :key="isHome ? 'home' : 'center'"
    v-model:openKeys="openKeys"
    v-model:selectedKeys="selectedKeys"
    mode="inline"
    class="side-user-bar"
    :inlineIndent="getEnv('area') === 'CN' ? 24 : 12"
    :inlineCollapsed="false"
    :disabledOverflow="true"
    :theme="currentTheme"
  >
    <li
      v-if="hasActionPermission('new_works_nav')"
      class="h-[44px] my-[16px] px-1"
    >
      <a-button
        type="primary"
        ghost
        class="w-full h-full flex items-center justify-center !bg-back-0"
        @click="handleCreateWork"
      >
        <template #icon>
          <iconpark-icon
            name="user-create"
            class="mr-[10px] text-[16px]"
          ></iconpark-icon>
        </template>
        {{ $t('chuang-jian-zuo-pin') }}
      </a-button>
    </li>

    <template v-for="item in navs">
      <a-menu-item
        v-if="initShow(item)"
        :key="item.route"
        @click="onItemClick(item)"
      >
        <template #icon>
          <span>
            <iconpark-icon v-if="area === 'CN'" :name="getIcon(item)" size="20">
            </iconpark-icon>
            <iconpark-icon v-else :name="item.iconGlobal" :size="28" />
          </span>
        </template>
        <nuxt-link :to="item.route" class="flex items-center">
          {{ item.name }}
          <div v-if="item.crown && isShow" class="upgrade ml-[8px]">
            {{ $t('sheng-ji') }}
          </div>
        </nuxt-link>
      </a-menu-item>
    </template>
  </a-menu>
</template>
<script lang="ts" setup>
import { USER_LEVEL_KEY } from '~/api/interface'
import { PageKey } from '~/constants/page'
import { useCreateWork } from '~/features/createWork'
import { useDetector } from '~/hooks/useDetector'
import { useCenterNav, useHomeNav, type Nav } from '~/hooks/useNav'

const route = useRoute()
const { isHome } = useDetector()
const selectedKeys = ref<string[]>()
const openKeys = ref<string[]>()
const centerNavs = useCenterNav()
const homeNavs = useHomeNav()
const userInfo = useUserInfo()

const currentTheme = useTheme()

const runtimeConfig = useRuntimeConfig()
const area = runtimeConfig.public.area

const { hasActionPermission } = usePageAndActionPermissionData()

const getIcon = (nav: Nav) => {
  const isMatched = route.path === nav.route
  if (isHome.value) {
    return (nav.icon || '') + (isMatched ? '-on' : '-off')
  }
  return nav.icon
}

const handleCreateWork = () => {
  if (!userInfo.value) {
    loginTask.add({
      type: IAfterLoginActionType.CREATE_WORK,
      payload: {},
    })
    navigateTo('/login?redirect=' + encodeURIComponent(location.href))
  } else {
    useCreateWork()
  }
}

const navs = computed(() =>
  isHome.value
    ? homeNavs.value
    : centerNavs.value.filter((v) => v.pageKey !== PageKey.PERSONAL_PAGE)
)

const isShow = computed(() =>
  userInfo.value?.rights_type
    ? [
        USER_LEVEL_KEY.TASTE,
        USER_LEVEL_KEY.PERSONAL,
        USER_LEVEL_KEY.PERSONAL_LITE,
      ].includes(Number(userInfo.value?.rights_type))
    : true
)

watch(
  () => route.path,
  (next, before) => {
    if (next) {
      let res = next
      const maxLength = 3

      if (next.split('/').length > maxLength) {
        res = next
          .split('/')
          .slice(0, next.split('/').length - 1)
          .join('/')
      }

      selectedKeys.value = [res.toString()]
    }
  },
  { immediate: true }
)

onMounted(() => {
  initKeys()
})

const initShow = (record: any) => {
  if (
    record.rights_type?.length &&
    record.permission?.length &&
    userInfo.value
  ) {
    const COMPANY = [
      USER_LEVEL_KEY.ORG_FLAGSHIP,
      USER_LEVEL_KEY.ORG_STANDARD,
      USER_LEVEL_KEY.TEAM_LIGHT,
      USER_LEVEL_KEY.TEAM_TASTE,
    ]
    if (COMPANY.includes(userInfo.value.rights_type)) {
      return (
        record.rights_type.includes(userInfo.value?.rights_type) &&
        record.permission.includes(userInfo.value?.permission)
      )
    }
    return record.rights_type.includes(userInfo.value?.rights_type)
  }

  return true
}

const initKeys = () => {
  const path: any = route.meta?.parent || route.path
  selectedKeys.value = [path]
  openKeys.value = []
}

function onItemClick(nav: Nav) {
  if (nav.trackEvent) {
    trackEvent(nav.trackEvent)
  }
}
</script>

<style lang="scss" global>
.side-user-bar {
  .ant-menu-item {
    font-weight: 600 !important;
  }
  .ant-menu-item.ant-menu-item-selected,
  .ant-menu-item:hover {
    background-color: var(--back-2) !important;
    border-radius: 12px !important;
  }
  .ant-menu-item a,
  .ant-menu-item a:hover {
    color: var(--text-1) !important;
  }
}
</style>

<style lang="scss" scoped>
.upgrade {
  height: 20px;
  width: 34px;
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 10px;
  color: #755c12;
  background: linear-gradient(225deg, #fff5d2 0%, #ffe7a6 100%);
}
.side-user-bar {
  padding: 20px 10px 0 16px;
  border-inline-end: none !important;

  &:deep(.ant-menu.ant-menu .ant-menu-item .ant-menu-item-icon) {
    line-height: normal;
  }

  &:deep(.ant-menu-item) {
    margin-top: 10px;
    margin-bottom: 10px;
    &:first-child {
      margin-top: 0;
    }
    .active {
      display: none;
    }
  }

  &:deep(.ant-menu-item-selected) {
    font-weight: 600;
  }
}
</style>
